/**
 * Pattern: Turtle Soup (Linda Raschke)
 *
 * Failed-breakout reversal: price makes a new N-bar extreme (e.g., a
 * 20-bar high), then within 1–3 bars closes BACK INSIDE the prior range.
 * The aborted breakout traps late-momentum buyers/sellers — Raschke's
 * classic counter-trend continuation setup.
 *
 * Useful in two contexts:
 *   1. As a SETUP for entries WITH the reversal (e.g., entry short after
 *      a failed-high turtle soup).
 *   2. As a COUNTER-SIGNAL when the trader was on the wrong side (entered
 *      long INTO a failed-high turtle soup) — surfaces "you bought a
 *      turtle soup setup that reversed against you."
 *
 * This detector returns the same shape either way; the consumer
 * interprets context (was the trade direction the same as the implied
 * reversal direction?).
 *
 * Pure function. No side effects.
 */

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

export const TURTLE_SOUP_EXTREME_LOOKBACK = 20;
export const TURTLE_SOUP_REVERSAL_BARS    = 3;  // close-back-inside must happen within this many bars

export const detectTurtleSoup = ({ bars, entryTs, direction, options = {} } = {}) => {
  const dir = normalizeDirection(direction);
  if (!dir) return null;
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(entryTs)) return null;

  const extremeLookback = options.extremeLookback || TURTLE_SOUP_EXTREME_LOOKBACK;
  const reversalBars = options.reversalBars || TURTLE_SOUP_REVERSAL_BARS;

  const entryIdx = findEntryBarIdx(bars, entryTs);
  // Need: at least extremeLookback bars BEFORE the breakout candidate.
  if (entryIdx < extremeLookback + reversalBars) return null;

  // Look backward up to `reversalBars` for a failed-breakout pattern that
  // resolved by the entry bar.
  for (let lookback = 1; lookback <= reversalBars; lookback++) {
    const breakoutIdx = entryIdx - lookback;
    if (breakoutIdx < extremeLookback) continue;
    const breakoutBar = bars[breakoutIdx];
    if (!breakoutBar) continue;

    // The prior N-bar window (excluding the breakout bar itself)
    const windowStart = breakoutIdx - extremeLookback;
    const windowEnd = breakoutIdx - 1;
    let priorHigh = -Infinity;
    let priorLow = Infinity;
    for (let i = windowStart; i <= windowEnd; i++) {
      const b = bars[i];
      const h = Number(b?.high);
      const l = Number(b?.low);
      if (Number.isFinite(h) && h > priorHigh) priorHigh = h;
      if (Number.isFinite(l) && l < priorLow) priorLow = l;
    }
    if (priorHigh === -Infinity || priorLow === Infinity) continue;

    const brkHi = Number(breakoutBar.high);
    const brkLo = Number(breakoutBar.low);

    // Two scenarios: failed-HIGH (new HoD) or failed-LOW (new LoD)
    const newHigh = brkHi > priorHigh;
    const newLow = brkLo < priorLow;

    if (newHigh) {
      // Failed-high turtle soup: implied reversal is DOWNWARD (short bias)
      // Check that the entry bar's close is back inside the prior range
      const entryClose = Number(bars[entryIdx]?.close);
      if (!Number.isFinite(entryClose)) continue;
      if (entryClose < priorHigh) {
        return {
          type: 'turtle-soup',
          score: 1,
          maxScore: 1,
          impliedDirection: 'short',
          alignsWithTrade: dir === 'short',
          details: `Failed-high breakout ${lookback} bar${lookback === 1 ? '' : 's'} ago (high ${brkHi} > prior-${extremeLookback}-bar high ${priorHigh}). Price closed back inside the range. ${dir === 'short' ? '✓ Trade aligned with the reversal.' : '⚠ Trade was AGAINST the reversal — you may have bought the failed break.'}`,
          evidence: { breakoutIdx, breakoutHigh: brkHi, priorHigh, priorLow, lookback, reversalDirection: 'short' },
        };
      }
    }

    if (newLow) {
      // Failed-low turtle soup: implied reversal is UPWARD (long bias)
      const entryClose = Number(bars[entryIdx]?.close);
      if (!Number.isFinite(entryClose)) continue;
      if (entryClose > priorLow) {
        return {
          type: 'turtle-soup',
          score: 1,
          maxScore: 1,
          impliedDirection: 'long',
          alignsWithTrade: dir === 'long',
          details: `Failed-low breakout ${lookback} bar${lookback === 1 ? '' : 's'} ago (low ${brkLo} < prior-${extremeLookback}-bar low ${priorLow}). Price closed back inside the range. ${dir === 'long' ? '✓ Trade aligned with the reversal.' : '⚠ Trade was AGAINST the reversal — you may have shorted the failed break.'}`,
          evidence: { breakoutIdx, breakoutLow: brkLo, priorHigh, priorLow, lookback, reversalDirection: 'long' },
        };
      }
    }
  }

  return null;
};
